import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

interface UserProfile {
  id: string;
  nickname: string;
  email: string;
  phone?: string;
  location?: string;
  skill_level?: string;
  bio?: string;
  created_at: string;
}

interface User {
  id: string;
  nickname: string;
  email: string;
  skill_level?: string;
  location?: string;
}

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  message: string;
  created_at: string;
  sender?: {
    nickname: string;
  };
}

interface Conversation {
  user: User;
  lastMessage?: Message;
  unreadCount: number;
}

interface Notification {
  id: string;
  message: string;
  type: 'message' | 'info';
  timestamp: number;
}

const Profile: React.FC = () => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  
  // Messages state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const [bookings] = useState([
    {
      id: 1,
      sessionName: t('morning_practice', 'Morning Practice'),
      court: t('central_court_a', 'Central Court A'),
      date: '2024-12-20',
      time: '09:00 AM',
      status: 'Confirmed'
    },
    {
      id: 2,
      sessionName: t('evening_tournament', 'Evening Tournament'),
      court: t('wan_chai_court_b', 'Wan Chai Court B'),
      date: '2024-12-22',
      time: '06:00 PM',
      status: 'Pending'
    }
  ]);

  const [scoreHistory] = useState([
    {
      id: 1,
      opponent: 'Sarah Chen',
      score: '11-9, 11-7',
      result: 'Win',
      date: '2024-12-18',
      court: t('central_court_a', 'Central Court A')
    },
    {
      id: 2,
      opponent: 'Mike Wong',
      score: '8-11, 11-6, 9-11',
      result: 'Loss',
      date: '2024-12-15',
      court: t('tsim_sha_tsui_court', 'Tsim Sha Tsui Court')
    }
  ]);

  // Add notification function
  const addNotification = (message: string, type: 'message' | 'info' = 'message') => {
    const notification: Notification = {
      id: Date.now().toString(),
      message,
      type,
      timestamp: Date.now()
    };
    
    setNotifications(prev => [notification, ...prev.slice(0, 4)]); // Keep only 5 notifications
    
    // Auto remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  // Real-time message subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('direct_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
          filter: `receiver_id=eq.${user.id}`
        },
        async (payload) => {
          const newMessage = payload.new as Message;
          
          // Get sender info
          const { data: senderData } = await supabase
            .from('profiles')
            .select('nickname')
            .eq('id', newMessage.sender_id)
            .single();

          // Add notification
          const senderName = senderData?.nickname || 'Someone';
          addNotification(`New message from ${senderName}`, 'message');
          
          // Update unread count
          setUnreadMessageCount(prev => prev + 1);
          
          // If currently viewing this conversation, add message to current view
          if (selectedConversation && selectedConversation.id === newMessage.sender_id) {
            setMessages(prev => [...prev, newMessage]);
          }
          
          // Update conversations list
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, selectedConversation]);

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          setProfile({
            id: user.id,
            nickname: user.email?.split('@')[0] || 'User',
            email: user.email || '',
            phone: '',
            location: 'Hong Kong',
            skill_level: 'Beginner',
            bio: '',
            created_at: user.created_at || new Date().toISOString()
          });
        } else {
          setProfile({
            ...data,
            email: user.email || data.email || ''
          });
        }
      } catch (error) {
        console.error('Error:', error);
        setProfile({
          id: user.id,
          nickname: user.email?.split('@')[0] || 'User',
          email: user.email || '',
          phone: '',
          location: 'Hong Kong',
          skill_level: 'Beginner',
          bio: '',
          created_at: user.created_at || new Date().toISOString()
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  // Fetch conversations when messages tab is active
  useEffect(() => {
    if (activeTab === 'messages' && user) {
      fetchConversations();
    }
  }, [activeTab, user]);

  // Search users
  useEffect(() => {
    if (searchQuery.trim() && showUserSearch) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, showUserSearch]);

  // Calculate unread messages count
  useEffect(() => {
    if (user && activeTab === 'messages') {
      const calculateUnreadCount = async () => {
        const { data, error } = await supabase
          .from('direct_messages')
          .select('id')
          .eq('receiver_id', user.id)
          .eq('is_read', false);

        if (!error && data) {
          setUnreadMessageCount(data.length);
        }
      };

      calculateUnreadCount();
    }
  }, [user, activeTab]);

  const fetchConversations = async () => {
    if (!user) return;

    try {
      // First, get all messages where user is sender or receiver
      const { data: messagesData, error: messagesError } = await supabase
        .from('direct_messages')
        .select('*')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (messagesError) {
        console.error('Error fetching messages:', messagesError);
        return;
      }

      if (!messagesData || messagesData.length === 0) {
        setConversations([]);
        return;
      }

      // Get unique user IDs from conversations
      const userIds = new Set<string>();
      messagesData.forEach((message) => {
        if (message.sender_id !== user.id) {
          userIds.add(message.sender_id);
        }
        if (message.receiver_id !== user.id) {
          userIds.add(message.receiver_id);
        }
      });

      // Fetch user profiles separately
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, nickname, email, skill_level, location')
        .in('id', Array.from(userIds));

      if (usersError) {
        console.error('Error fetching users:', usersError);
        return;
      }

      // Create user lookup map
      const userMap = new Map<string, User>();
      usersData?.forEach((userData) => {
        userMap.set(userData.id, userData);
      });

      // Group messages by conversation partner
      const conversationMap = new Map<string, Conversation>();
      
      messagesData.forEach((message) => {
        const isFromCurrentUser = message.sender_id === user.id;
        const partnerId = isFromCurrentUser ? message.receiver_id : message.sender_id;
        const conversationPartner = userMap.get(partnerId);
        
        if (!conversationPartner) return;

        if (!conversationMap.has(partnerId)) {
          // Count unread messages from this partner
          const unreadCount = messagesData.filter(m => 
            m.sender_id === partnerId && 
            m.receiver_id === user.id && 
            !m.is_read
          ).length;

          conversationMap.set(partnerId, {
            user: conversationPartner,
            lastMessage: message,
            unreadCount
          });
        } else {
          const existing = conversationMap.get(partnerId)!;
          if (!existing.lastMessage || new Date(message.created_at) > new Date(existing.lastMessage.created_at)) {
            existing.lastMessage = message;
          }
        }
      });

      setConversations(Array.from(conversationMap.values()));
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const searchUsers = async () => {
    if (!user || !searchQuery.trim()) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nickname, email, skill_level, location')
        .neq('id', user.id)
        .or(`nickname.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
        .limit(10);

      if (error) {
        console.error('Error searching users:', error);
        return;
      }

      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const startConversation = async (selectedUser: User) => {
    setSelectedConversation(selectedUser);
    setShowUserSearch(false);
    setSearchQuery('');
    await fetchMessages(selectedUser.id);
    
    // Mark messages as read when opening conversation
    await markMessagesAsRead(selectedUser.id);
  };

  const markMessagesAsRead = async (senderId: string) => {
    if (!user) return;

    try {
      await supabase
        .from('direct_messages')
        .update({ is_read: true })
        .eq('sender_id', senderId)
        .eq('receiver_id', user.id)
        .eq('is_read', false);

      // Update local unread count
      setUnreadMessageCount(prev => {
        const conversation = conversations.find(c => c.user.id === senderId);
        return prev - (conversation?.unreadCount || 0);
      });

      // Update conversations
      setConversations(prev => 
        prev.map(conv => 
          conv.user.id === senderId 
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      );
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const fetchMessages = async (conversationId: string) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('direct_messages')
      .select(`
        id,
        sender_id,
        receiver_id,
        message,
        is_read,
        created_at
      `)
      .or(`and(sender_id.eq.${user.id},receiver_id.eq.${conversationId}),and(sender_id.eq.${conversationId},receiver_id.eq.${user.id})`)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
      return;
    }

    setMessages(data || []);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !user) return;

    const messageContent = newMessage.trim();
    setNewMessage(''); // Clear input immediately for better UX

    try {
      const { data, error } = await supabase
        .from('direct_messages')
        .insert({
          sender_id: user.id,
          receiver_id: selectedConversation.id,
          message: messageContent
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending message:', error);
        // Restore message if failed
        setNewMessage(messageContent);
        alert('Failed to send message. Please try again.');
        return;
      }

      // Add message to local state immediately for better UX
      const newMessageObj: Message = {
        id: data.id,
        sender_id: user.id,
        receiver_id: selectedConversation.id,
        message: data.message,
        created_at: data.created_at
      };

      setMessages(prev => [...prev, newMessageObj]);
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageContent);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSaveProfile = async () => {
    if (!user || !profile) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          email: profile.email,
          nickname: profile.nickname,
          phone: profile.phone,
          location: profile.location,
          skill_level: profile.skill_level,
          bio: profile.bio,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error updating profile:', error);
        alert(t('failed_to_save_profile', 'Failed to save profile. Please try again.'));
      } else {
        setIsEditing(false);
        alert(t('profile_updated_successfully', 'Profile updated successfully!'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert(t('failed_to_save_profile', 'Failed to save profile. Please try again.'));
    }
  };

  const handleCancelBooking = (bookingId: number) => {
    console.log('Canceling booking:', bookingId);
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading_profile', 'Loading profile...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      {/* Notification Toast */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="bg-white border-l-4 border-teal-500 rounded-lg shadow-lg p-4 max-w-sm animate-slide-in-right"
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <i className={`${notification.type === 'message' ? 'ri-message-line' : 'ri-information-line'} text-teal-500`}></i>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(notification.timestamp).toLocaleTimeString()}
                  </p>
                </div>
                <button
                  onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
                  className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Notification Bell */}
      {(notifications.length > 0 || unreadMessageCount > 0) && (
        <div className="fixed top-20 right-4 z-40">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative bg-teal-600 text-white p-3 rounded-full shadow-lg hover:bg-teal-700 transition-colors cursor-pointer"
          >
            <i className="ri-notification-line text-lg"></i>
            {(notifications.length > 0 || unreadMessageCount > 0) && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {Math.min(notifications.length + unreadMessageCount, 9)}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifications && (
            <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">{t('notifications', 'Notifications')}</h3>
                  {notifications.length > 0 && (
                    <button
                      onClick={clearNotifications}
                      className="text-xs text-teal-600 hover:text-teal-700 cursor-pointer"
                    >
                      {t('clear_all', 'Clear All')}
                    </button>
                  )}
                </div>
              </div>
              
              <div className="max-h-64 overflow-y-auto">
                {notifications.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {notifications.map((notification) => (
                      <div key={notification.id} className="p-3 hover:bg-gray-50">
                        <div className="flex items-start space-x-3">
                          <i className={`${notification.type === 'message' ? 'ri-message-line' : 'ri-information-line'} text-teal-500 mt-1`}></i>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">{notification.message}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(notification.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : unreadMessageCount > 0 ? (
                  <div className="p-4 text-center">
                    <i className="ri-message-line text-2xl text-teal-500 mb-2"></i>
                    <p className="text-sm text-gray-600">
                      {t('unread_messages', `You have ${unreadMessageCount} unread message${unreadMessageCount > 1 ? 's' : ''}`)}
                    </p>
                    <button
                      onClick={() => {
                        setActiveTab('messages');
                        setShowNotifications(false);
                      }}
                      className="mt-2 text-xs text-teal-600 hover:text-teal-700 cursor-pointer"
                    >
                      {t('view_messages', 'View Messages')}
                    </button>
                  </div>
                ) : (
                  <div className="p-4 text-center">
                    <p className="text-sm text-gray-500">{t('no_notifications', 'No new notifications')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center">
              <span className="text-xl sm:text-2xl font-bold text-white">
                {profile.nickname.split(' ').map(n => n[0]).join('').toUpperCase()}
              </span>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{profile.nickname}</h1>
              <p className="text-gray-600 text-sm sm:text-base">{profile.email}</p>
              <p className="text-xs sm:text-sm text-gray-500">{t('member_since', 'Member since')} {new Date(profile.created_at).toLocaleDateString()}</p>
            </div>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="w-full sm:w-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-edit-line mr-2"></i>
              {isEditing ? t('cancel', 'Cancel') : t('edit_profile', 'Edit Profile')}
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto px-4 sm:px-6">
              {[
                { id: 'profile', label: t('profile_info', 'Profile Info'), icon: 'ri-user-line' },
                { id: 'bookings', label: t('my_bookings', 'My Bookings'), icon: 'ri-calendar-line' },
                { id: 'scores', label: t('score_history', 'Score History'), icon: 'ri-trophy-line' },
                { 
                  id: 'messages', 
                  label: t('messages', 'Messages'), 
                  icon: 'ri-message-line',
                  badge: unreadMessageCount > 0 ? unreadMessageCount : undefined
                }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex-shrink-0 py-3 sm:py-4 px-2 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={`${tab.icon} mr-1 sm:mr-2`}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                  {tab.badge && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {Math.min(tab.badge, 9)}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('profile_information', 'Profile Information')}</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('nickname', 'Nickname')}</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile.nickname}
                      onChange={(e) => setProfile({...profile, nickname: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  ) : (
                    <p className="text-gray-900 text-sm sm:text-base">{profile.nickname}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('email', 'Email')}</label>
                  <p className="text-gray-900 text-sm sm:text-base break-all">{profile.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('phone', 'Phone')}</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={profile.phone || ''}
                      onChange={(e) => setProfile({...profile, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder={t('enter_your_phone_number', 'Enter your phone number')}
                    />
                  ) : (
                    <p className="text-gray-900 text-sm sm:text-base">{profile.phone || t('not_provided', 'Not provided')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('location', 'Location')}</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile.location || ''}
                      onChange={(e) => setProfile({...profile, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder={t('enter_your_location', 'Enter your location')}
                    />
                  ) : (
                    <p className="text-gray-900 text-sm sm:text-base">{profile.location || t('not_provided', 'Not provided')}</p>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('skill_level', 'Skill Level')}</label>
                  {isEditing ? (
                    <select
                      value={profile.skill_level || 'Beginner'}
                      onChange={(e) => setProfile({...profile, skill_level: e.target.value})}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    >
                      <option value="Beginner">{t('beginner', 'Beginner')}</option>
                      <option value="Intermediate">{t('intermediate', 'Intermediate')}</option>
                      <option value="Advanced">{t('advanced', 'Advanced')}</option>
                      <option value="Expert">{t('expert', 'Expert')}</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 text-sm sm:text-base">{t(profile.skill_level?.toLowerCase() || 'beginner', profile.skill_level || 'Beginner')}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('bio', 'Bio')}</label>
                {isEditing ? (
                  <textarea
                    value={profile.bio || ''}
                    onChange={(e) => setProfile({...profile, bio: e.target.value})}
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder={t('tell_us_about_yourself', 'Tell us about yourself...')}
                  />
                ) : (
                  <p className="text-gray-900 text-sm sm:text-base">{profile.bio || t('no_bio_provided_yet', 'No bio provided yet.')}</p>
                )}
              </div>

              {isEditing && (
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                  <button
                    onClick={handleSaveProfile}
                    className="w-full sm:w-auto px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    {t('save_changes', 'Save Changes')}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="w-full sm:w-auto px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    {t('cancel', 'Cancel')}
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'bookings' && (
            <div className="space-y-4 sm:space-y-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('my_bookings', 'My Bookings')}</h2>
              
              <div className="space-y-3 sm:space-y-4">
                {bookings.map((booking) => (
                  <div key={booking.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{booking.sessionName}</h3>
                        <p className="text-gray-600 text-sm">{booking.court}</p>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {new Date(booking.date).toLocaleDateString()} {t('at', 'at')} {booking.time}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'Confirmed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {t(booking.status.toLowerCase(), booking.status)}
                        </span>
                        <button
                          onClick={() => handleCancelBooking(booking.id)}
                          className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                        >
                          {t('cancel', 'Cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'scores' && (
            <div className="space-y-4 sm:space-y-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('score_history', 'Score History')}</h2>
              
              <div className="space-y-3 sm:space-y-4">
                {scoreHistory.map((match) => (
                  <div key={match.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{t('vs', 'vs')} {match.opponent}</h3>
                        <p className="text-gray-600 text-sm">{match.score}</p>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {new Date(match.date).toLocaleDateString()} {t('at', 'at')} {match.court}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          match.result === 'Win' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {t(match.result.toLowerCase(), match.result)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('messages', 'Messages')}</h2>
                <button
                  onClick={() => setShowUserSearch(!showUserSearch)}
                  className="w-full sm:w-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-add-line mr-2"></i>
                  {t('new_conversation', 'New Conversation')}
                </button>
              </div>

              {showUserSearch && (
                <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50">
                  <h3 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">{t('find_users', 'Find Users')}</h3>
                  <div className="relative mb-4">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('search_users', 'Search users...')}
                      className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                    <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                  
                  {searchResults.length > 0 ? (
                    <div className="space-y-2">
                      {searchResults.map((searchUser) => (
                        <div
                          key={searchUser.id}
                          onClick={() => startConversation(searchUser)}
                          className="flex items-center space-x-3 p-3 bg-white rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-xs sm:text-sm font-bold text-white">
                              {searchUser.nickname.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{searchUser.nickname}</p>
                            <p className="text-xs text-gray-500 truncate">{searchUser.skill_level} • {searchUser.location}</p>
                          </div>
                          <button className="px-2 sm:px-3 py-1 bg-teal-600 text-white rounded text-xs sm:text-sm hover:bg-teal-700 whitespace-nowrap">
                            {t('start_conversation', 'Start')}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : searchQuery.trim() ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500 text-sm">{t('no_users_found', 'No users found')}</p>
                      <p className="text-xs text-gray-400">{t('try_different_search', 'Try a different search term')}</p>
                    </div>
                  ) : null}
                </div>
              )}

              {selectedConversation ? (
                <div className="border border-gray-200 rounded-lg">
                  {/* Conversation Header */}
                  <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setSelectedConversation(null)}
                        className="p-1 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <i className="ri-arrow-left-line text-gray-600"></i>
                      </button>
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-xs sm:text-sm font-bold text-white">
                          {selectedConversation.nickname.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{selectedConversation.nickname}</p>
                        <p className="text-xs sm:text-sm text-gray-500">{selectedConversation.skill_level}</p>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="h-64 sm:h-96 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
                    {messagesLoading ? (
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                      </div>
                    ) : messages.length > 0 ? (
                      <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((message) => (
                          <div
                            key={message.id}
                            className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                message.sender_id === user?.id
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-200 text-gray-800'
                              }`}
                            >
                              <p className="text-sm">{message.message}</p>
                              <p className="text-xs opacity-70 mt-1">
                                {new Date(message.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500 text-sm">{t('no_messages_yet', 'No messages yet')}</p>
                        <p className="text-xs text-gray-400">{t('start_conversation', 'Start a conversation!')}</p>
                      </div>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-3 sm:p-4 border-t border-gray-200">
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={t('type_your_message', 'Type your message...')}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        maxLength={1000}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim()}
                        className="px-3 sm:px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center justify-center"
                        title={t('send_message', 'Send message')}
                      >
                        <i className="ri-send-plane-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ) : conversations.length > 0 ? (
                <div className="space-y-2">
                  {conversations.map((conversation) => (
                    <div
                      key={conversation.user.id}
                      onClick={() => startConversation(conversation.user)}
                      className="flex items-center space-x-3 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-xs sm:text-sm font-bold text-white">
                          {conversation.user.nickname.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{conversation.user.nickname}</p>
                        {conversation.lastMessage && (
                          <p className="text-xs sm:text-sm text-gray-500 truncate">
                            {conversation.lastMessage.message}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        {conversation.lastMessage && (
                          <p className="text-xs text-gray-400">
                            {new Date(conversation.lastMessage.created_at).toLocaleDateString()}
                          </p>
                        )}
                        {conversation.unreadCount > 0 && (
                          <span className="inline-block w-5 h-5 bg-teal-600 text-white text-xs rounded-full text-center leading-5">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <i className="ri-message-line text-3xl sm:text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-500 text-sm sm:text-base">{t('no_messages_yet', 'No messages yet')}</p>
                  <p className="text-xs sm:text-sm text-gray-400">{t('conversations_will_appear_here', 'Your conversations with other members will appear here')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
